<template>
  <div class="chart-container">
    <Doughnut v-if="hasData" :data="chartData" :options="chartOptions" />
    <div v-else class="no-data-message">
      Aucune donnée disponible pour le moment
    </div>
  </div>
</template>

<script setup>
import { Doughnut } from 'vue-chartjs'
import { Chart as ChartJS, Title, Tooltip, Legend, ArcElement } from 'chart.js'
import { computed } from 'vue'

ChartJS.register(Title, Tooltip, Legend, ArcElement)

const props = defineProps({
  stats: {
    type: Array,
    required: true,
    default: () => []
  }
})

// Calculer les totaux par statut
const statusTotals = computed(() => {
  const totals = {
    pending: 0,
    pending_onarrival: 0,
    confirmed: 0,
    partially_paid: 0,
    cancelled: 0,
    payment_failed: 0,
    expired: 0
  }

  props.stats.forEach(monthData => {
    for (const status in monthData.stats) {
      if (monthData.stats[status]?.count) {
        totals[status] += monthData.stats[status].count
      }
    }
  })

  return totals
})

const hasData = computed(() => {
  return Object.values(statusTotals.value).some(count => count > 0)
})

const chartData = computed(() => {
  const labels = []
  const data = []
  const backgroundColors = []

  // Couleurs correspondantes
  const colorMap = {
    pending: '#F59E0B',
    pending_onarrival: '#D4AF37',
    confirmed: '#10B981',
    partially_paid: '#D4AF37',
    cancelled: '#EF4444',
    payment_failed: '#8B5CF6',
    expired: '#9CA3AF'
  }

  // Texte des labels
  const labelMap = {
    pending: 'En attente',
    pending_onarrival: 'Paiement sur place',
    confirmed: 'Confirmées',
    partially_paid: 'Partiellement payées',
    cancelled: 'Annulées',
    payment_failed: 'Échec paiement',
    expired: 'Expirées'
  }

  // Ajouter seulement les statuts avec des données
  for (const status in statusTotals.value) {
    if (statusTotals.value[status] > 0) {
      labels.push(labelMap[status])
      data.push(statusTotals.value[status])
      backgroundColors.push(colorMap[status])
    }
  }

  return {
    labels,
    datasets: [{
      data,
      backgroundColor: backgroundColors,
      borderWidth: 1,
      hoverOffset: 10
    }]
  }
})

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'right',
      labels: {
        boxWidth: 12,
        padding: 20,
        usePointStyle: true,
        pointStyle: 'circle'
      }
    },
    tooltip: {
      callbacks: {
        label: (context) => {
          const label = context.label || ''
          const value = context.raw || 0
          const total = context.dataset.data.reduce((a, b) => a + b, 0)
          const percentage = Math.round((value / total) * 100)
          return `${label}: ${value} (${percentage}%)`
        }
      }
    }
  },
  cutout: '70%'
}
</script>

<style scoped>
.chart-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.no-data-message {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #666;
  font-style: italic;
}
</style>